import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import { interimApi } from 'api/InterimApi';
import { getServices } from 'api/Service';

import { formatDate, ia } from 'lib/helpers/utility';

import CPTRow from 'components/practice/charts/CPTRow';
import {
  calculateTotalCharge,
  getPlaceOfService,
  handleCPTSearch
} from 'components/practice/charts/SuperBillPage/lib';
import { handleSelectCPT } from 'components/practice/charts/SuperBillPage/lib/handleSelectCPT';
import { currentPractice } from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Input from 'components/shared/Input/Input';

import ICDRow from './ICDRow';

const ServicesAndDiagnosis = ({
  formik,
  superbill,
  states,
  procedures,
  setProcedures,
  handleSubmitClaim
}) => {
  const [posOptions, setPosOptions] = useState([]);
  const practice = useRecoilValue(currentPractice);
  const { values: claim, setFieldValue, errors, handleBlur, touched } = formik;
  const navigate = useNavigate();

  useEffect(() => {
    if (states) {
      getPOS();
    }
  }, []);

  const getPOS = async () => {
    try {
      const res = await interimApi('/api/codes/place_of_service/get', {});
      if (res.data) {
        setPosOptions(res.data.place_of_service);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const updateDiagnosis = (e, key) => {
    setFieldValue([key], e?.value ? e?.value : '');
  };

  const handleProcedureChanges = (e, index) => {
    const newVal = e.target.value;
    const fieldName = e.target.name;

    setProcedures((prev) => {
      prev[index][fieldName] = newVal;
      return [...prev];
    });

    setFieldValue('total_charge', calculateTotalCharge(procedures));
  };

  const convertProcedureToPrimary = (index) => {
    setProcedures((prev) => {
      prev[index] = {
        ...prev[index],
        type: 'primary',
        charge_type: 'copay'
      };

      return [...prev];
    });
  };

  const handleDeletion = (index) => {
    const currentCPT = [...procedures];
    currentCPT.splice(index, 1);
    setFieldValue('total_charge', calculateTotalCharge(procedures));

    setProcedures(currentCPT);
    setFieldValue('procedures', currentCPT);
  };

  const selectCPT = async (e, index) => {
    const data = await getServices(navigate, { cpt: e.value });
    const service = data?.services;
    const updatedProcedures = handleSelectCPT({
      procedures,
      index,
      service,
      e,
      superbill,
      practice
    });

    setProcedures(updatedProcedures);

    setFieldValue('procedures', updatedProcedures);
    setFieldValue('total_charge', calculateTotalCharge(procedures));
  };

  const addCPT = () => {
    const dos = formatDate(superbill?.encounter?.appointment?.starts_at, practice?.timezone);

    const newCPT = {
      procedure_code: null,
      modifiers: null,
      charge: 0,
      diagnosis: null,
      from_date: dos,
      thru_date: dos,
      narrative: '',
      units: '1',
      place_of_service: getPlaceOfService(superbill, practice)
    };
    const currentCPT = [...procedures];
    currentCPT.push(newCPT);

    setProcedures(currentCPT);
    setFieldValue('total_charge', calculateTotalCharge(procedures));
  };

  const handleInputChange = (e) => {
    setFieldValue([e.target.name], e.target.value);
  };

  const handleClaimNavigate = () => {
    if (claim?.id && claim?.state !== 'deleted') {
      navigate(`/portal/beyond-billing/claims/${claim?.id}`, { state: { claimID: claim?.id } });
    } else {
      showAlert({ color: 'danger', message: 'Claim not found or may have been deleted.' });
    }
  };

  const buttonLabel = ['draft', 'deleted'].includes(claim?.state) ? 'Ready for claim' : 'Update';

  return (
    <div className="block rounded-md bg-white p-[1.25rem] shadow">
      <div className="flex items-center justify-between gap-4">
        <label className="text-xl font-400 text-neutral-800">Services & Diagnosis</label>
      </div>
      <ICDRow claim={claim} updateDiagnosis={updateDiagnosis} showTitle />

      <div className="block">
        <div className="!pb-2 !pt-6">
          <p className="!pb-3">Services (CPT &#174; CODES)</p>
          <div className="grid gap-2">
            {procedures?.map((item, index) => {
              return (
                <CPTRow
                  item={item}
                  key={procedures.length + index}
                  index={index}
                  handleProcedureChanges={handleProcedureChanges}
                  handleDeletion={handleDeletion}
                  posOptions={posOptions?.map((item) => {
                    return { label: `${item.code} - ${item.name}`, value: item.code };
                  })}
                  handleCPTSearch={(a, b) => handleCPTSearch(a, b)}
                  selectCPT={selectCPT}
                  superbill={superbill}
                  convertProcedureToPrimary={convertProcedureToPrimary}
                />
              );
            })}
          </div>
        </div>
        {ia(procedures) && (
          <div className="flex items-center gap-x-4 pb-2">
            <>
              <label className="!mt-4 text-lg">{`Total Charge: $${calculateTotalCharge(procedures)},`}</label>
              <label className="!mt-4 text-lg">{`Insurance Total Charge: $${calculateTotalCharge(procedures, true)}`}</label>
            </>
          </div>
        )}
        <Button
          onClick={() => addCPT()}
          text="Add"
          iconColor="white"
          icon="add-linear"
          color="success"
          outlined
          className="min-w-[150px]"
        />
      </div>
      <div className="flex sm:flex-col md:items-center">
        <div className="p-3 print:hidden">
          <Input
            label="Custom Claim Id"
            placeholder="Custom Claim Id"
            name="custom_id"
            value={claim?.custom_id}
            onChange={handleInputChange}
            id="custom_id"
            onBlur={handleBlur}
            error={touched.custom_id && errors.custom_id}
          />
        </div>
      </div>

      <div className="flex justify-end !gap-5">
        {superbill.state === 'complete' && (
          <Button
            color="neutral"
            text="Go to claim"
            className="print:hidden"
            onClick={handleClaimNavigate}
          />
        )}
        <Button
          color="success"
          text={buttonLabel}
          className="print:hidden"
          onClick={handleSubmitClaim}
        />
      </div>
    </div>
  );
};

export default withErrorBoundary(ServicesAndDiagnosis);
